import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { generatePaymentSlip } from '@/utils/pdfGenerator';
import { format } from 'date-fns';
import { Download, Printer } from "lucide-react";
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const ManualPaymentStep = ({ oop, formData, onBack }) => {
   const navigate = useNavigate();
   const printRef = React.useRef();

   const handlePrintSlip = () => {
      const printContent = document.createElement('div');
      printContent.innerHTML = `
         <style>
            @media print {
               @page {
                  size: A4;
                  margin: 20mm;
               }
               body {
                  font-family: Arial, sans-serif;
                  color: #000;
                  background: #fff;
               }
               .print-only {
                  display: block !important;
               }
               .no-print {
                  display: none !important;
               }
            }
         </style>
         <div class="print-only" style="padding: 20px;">
            <div style="text-align: center; margin-bottom: 30px;">
               <h1 style="font-size: 24px; margin-bottom: 10px;">PAYMENT SLIP</h1>
               <h2 style="font-size: 16px; font-weight: normal;">Department of Environment and Natural Resources</h2>
               <h3 style="font-size: 14px; font-weight: normal;">PENRO Marinduque</h3>
            </div>

            <div style="margin-bottom: 30px;">
               <h3 style="font-size: 16px; margin-bottom: 15px;">Payment Details</h3>
               <table style="width: 100%; border-collapse: collapse;">
                  <tr>
                     <td style="padding: 8px 0;">Bill Number:</td>
                     <td style="text-align: right;">${oop?.billNo}</td>
                  </tr>
                  <tr>
                     <td style="padding: 8px 0;">Amount to Pay:</td>
                     <td style="text-align: right;">₱${oop?.totalAmount?.toFixed(2)}</td>
                  </tr>
                  <tr>
                     <td style="padding: 8px 0;">Due Date:</td>
                     <td style="text-align: right;">${format(new Date(Date.now() + 3 * 24 * 60 * 60 * 1000), 'MMMM d, yyyy')}</td>
                  </tr>
                  <tr>
                     <td style="padding: 8px 0;">Payee Name:</td>
                     <td style="text-align: right;">${formData?.fullName}</td>
                  </tr>
               </table>
            </div>

            <div style="margin-bottom: 30px;">
               <h3 style="font-size: 16px; margin-bottom: 15px;">DENR Office Information</h3>
               <p style="margin: 5px 0;">Address: DENR Regional Office</p>
               <p style="margin: 5px 0;">Office Hours: Monday to Friday, 8:00 AM - 5:00 PM</p>
               <p style="margin: 5px 0;">Payment Window: Cashier's Office, Ground Floor</p>
               <p style="margin: 5px 0;">Contact: (042) 332-1490</p>
            </div>

            <div style="background-color: #fff9e6; padding: 15px; border: 1px solid #ffeeba; margin-bottom: 30px;">
               <h3 style="font-size: 16px; margin-bottom: 10px; color: #856404;">Important Notes</h3>
               <ul style="margin: 0; padding-left: 20px; color: #856404;">
                  <li>Please bring a valid ID</li>
                  <li>Payment must be made within 3 working days</li>
                  <li>Keep your official receipt for future reference</li>
                  <li>Present this payment slip to the cashier</li>
               </ul>
            </div>
         </div>
      `;

      const printWindow = window.open('', '_blank');
      printWindow.document.write(printContent.innerHTML);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
   };

   const handleDownloadSlip = async () => {
      try {
         const pdfBlob = await generatePaymentSlip({
            oop,
            paymentDetails: formData,
            officeDetails: {
               address: "DENR Regional Office, Capitol Compound, Bangbangalon, Boac, Marinduque",
               officeHours: "Monday to Friday, 8:00 AM - 5:00 PM",
               contactNumber: "(042) 332-1490"
            }
         });

         const url = window.URL.createObjectURL(pdfBlob);
         const link = document.createElement('a');
         link.href = url;
         link.download = `Payment_Slip_${oop.billNo}.pdf`;
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         window.URL.revokeObjectURL(url);
      } catch (error) {
         console.error('Error generating PDF:', error);
         toast.error("Failed to generate payment slip");
      }
   };

   const handleDone = async () => {
      try {
         navigate('/applicationsStatus?view=oops', {
            replace: true,
            state: {
               message: 'Payment slip generated successfully. Please proceed to the DENR office for payment.'
            }
         });
      } catch (error) {
         console.error('Error updating status:', error);
         toast.error('Failed to update payment status');
      }
   };

   return (
      <div className="w-full max-w-2xl mx-auto">
         <Card className="bg-white dark:bg-black">
            <CardHeader>
               <CardTitle className="text-center text-gray-900 dark:text-gray-100">Payment Instructions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
               <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-100 dark:border-gray-700">
                  <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Important Notes</h3>
                  <ul className="list-disc list-inside space-y-2 text-gray-600 dark:text-gray-300">
                     <li>Please bring a valid ID</li>
                     <li>Payment must be made within 3 working days</li>
                     <li>Keep your official receipt for future reference</li>
                     <li>Present this payment slip to the cashier</li>
                  </ul>
               </div>

               <div className="flex flex-col gap-3">
                  <div className="flex gap-3">
                     <Button
                        onClick={handlePrintSlip}
                        className="flex-1 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                        variant="outline"
                     >
                        <Printer className="w-4 h-4 mr-2" />
                        Print Slip
                     </Button>
                     <Button
                        onClick={handleDownloadSlip}
                        className="flex-1 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                        variant="outline"
                     >
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                     </Button>
                  </div>
                  <div className="flex gap-3">
                     <Button
                        onClick={onBack}
                        variant="outline"
                        className="flex-1 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                     >
                        Back
                     </Button>
                     <Button
                        onClick={handleDone}
                        className="flex-1 bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600"
                     >
                        Done
                     </Button>
                  </div>
               </div>
            </CardContent>
         </Card>
      </div>
   );
};

export default ManualPaymentStep;
